import React, { useEffect, useState, useRef } from 'react';

/**
 * AnimatedCounter — count-up animation from 0 to target value.
 * Optimized with requestAnimationFrame for buttery-smooth motion.
 *
 * Props:
 * - value: target number
 * - duration: ms (default 1400)
 * - prefix: string before (e.g. "+", "-")
 * - suffix: string after (e.g. "€", " €/an", "%")
 * - format: "fr" | "compact" (default "fr" = 17 200)
 * - className: extra classes
 */
export default function AnimatedCounter({
    value = 0,
    duration = 1400,
    prefix = '',
    suffix = '',
    format = 'fr',
    className = '',
}) {
    const [display, setDisplay] = useState(0);
    const startRef = useRef(null);
    const rafRef = useRef(null);

    useEffect(() => {
        // Cancel previous animation if any
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        startRef.current = null;
        const target = Number(value) || 0;
        const startValue = 0;

        const step = (timestamp) => {
            if (startRef.current === null) startRef.current = timestamp;
            const elapsed = timestamp - startRef.current;
            const progress = Math.min(elapsed / duration, 1);
            // ease-out cubic for "snap-to" feel
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = Math.round(startValue + (target - startValue) * eased);
            setDisplay(current);
            if (progress < 1) {
                rafRef.current = requestAnimationFrame(step);
            }
        };
        rafRef.current = requestAnimationFrame(step);

        return () => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [value, duration]);

    const formatted =
        format === 'compact' && Math.abs(display) >= 1000
            ? `${(display / 1000).toFixed(1)}k`
            : new Intl.NumberFormat('fr-FR').format(display);

    return (
        <span className={className} data-testid="animated-counter">
            {prefix}
            {formatted}
            {suffix}
        </span>
    );
}
